import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faCheckCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/shared/Button.styled';
import ButtonLink from 'components/shared/ButtonLink.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import Modal from 'components/shared/Modal';
import { StyledModalActions } from 'components/shared/Modal.styled';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { PublicManfrediteJobApplicationResume } from 'interfaces/manfredite.interface';
import { useRouter } from 'next/router';
import { useState } from 'react';
import ReactModal from 'react-modal';
import { getTranslatedText } from 'services/multilingual.service';
import { getExperienceLabel } from 'services/ui/profile.service';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { config } from 'utils/config';
import { getErrorMessage } from 'utils/errors';
import {
  GlobalStyles,
  StyledInfo,
  StyledSubtitle,
  StyledTerms,
  StyledWrapper
} from './CVApplicationsTermsModalComponent.styled';

type Props = {
  isOpen: boolean;
  onAccept?: () => Promise<void>;
  jobApplicationResume: PublicManfrediteJobApplicationResume;
  alreadyAccepted?: boolean;
  close?: () => void;
};

const CVApplicationsTermsModalComponent: React.FC<Props> = ({
  isOpen,
  jobApplicationResume,
  onAccept,
  alreadyAccepted = false,
  close
}) => {
  const { t } = useI18n('cv-applications-view');
  const { locale } = useRouter();

  const { addErrorToast } = useToasts();

  const [isLoading, setIsLoading] = useState(false);

  const moreInfoLink = `${config.appUrl}/${locale}${getAppUrl(
    APP_URLS.cvApplicationsLanding,
    locale
  )}#jobAppsCompanies`;

  const title = alreadyAccepted
    ? t('cvApplicationsView.modalTerms.titleAlreadyAccepted')
    : t('cvApplicationsView.modalTerms.title', {
        fullName:
          `${jobApplicationResume.firstName} ${jobApplicationResume.lastName}`.trim()
      });

  const roles = jobApplicationResume.roles?.map((role) =>
    getTranslatedText(role.name, locale)
  );

  const experience = jobApplicationResume.experience
    ? getExperienceLabel(jobApplicationResume.experience, locale)
    : undefined;

  const position = getTranslatedText(jobApplicationResume.jobPosition, locale);

  const company = getTranslatedText(jobApplicationResume.companyName, locale);

  const onAcceptClick = async (): Promise<void> => {
    try {
      if (!onAccept) {
        return;
      }

      setIsLoading(true);

      await onAccept();

      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      addErrorToast(
        t('cvApplicationsView.modalTerms.messages.error.acceptTerms', {
          error: getErrorMessage(ex)
        })
      );
    }
  };

  return (
    <ReactModal isOpen={isOpen} contentLabel={title} onRequestClose={close}>
      <Modal title={title} onClose={close}>
        <StyledWrapper>
          {!alreadyAccepted && (
            <>
              <StyledInfo>
                <h5>{t('cvApplicationsView.modalTerms.info.title')}</h5>

                {(roles || experience) && (
                  <ul>
                    {roles && (
                      <li>
                        <HtmlTrans
                          ns="cv-applications-view"
                          i18nKey="cvApplicationsView.modalTerms.info.roles"
                          values={{
                            roles
                          }}
                        />
                      </li>
                    )}

                    {experience && (
                      <li>
                        <HtmlTrans
                          ns="cv-applications-view"
                          i18nKey="cvApplicationsView.modalTerms.info.experience"
                          values={{
                            experience
                          }}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </StyledInfo>

              <StyledSubtitle>
                {t('cvApplicationsView.modalTerms.terms.title')}
              </StyledSubtitle>
            </>
          )}

          <StyledTerms>
            <li>
              <HtmlTrans
                ns="cv-applications-view"
                i18nKey="cvApplicationsView.modalTerms.terms.term1"
                values={{
                  company
                }}
              />
            </li>

            <li>
              {position && (
                <HtmlTrans
                  ns="cv-applications-view"
                  i18nKey="cvApplicationsView.modalTerms.terms.term2"
                  values={{
                    position,
                    company
                  }}
                />
              )}

              {!position && (
                <HtmlTrans
                  ns="cv-applications-view"
                  i18nKey="cvApplicationsView.modalTerms.terms.term2_noposition"
                  values={{
                    company
                  }}
                />
              )}
            </li>

            <li>
              <HtmlTrans
                ns="cv-applications-view"
                i18nKey="cvApplicationsView.modalTerms.terms.term3"
              />
            </li>
          </StyledTerms>

          {!alreadyAccepted && (
            <StyledModalActions>
              <Button onClick={onAcceptClick} isLoading={isLoading}>
                {!isLoading && <FontAwesomeIcon icon={faCheckCircle} />}
                {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}

                <span>{t('cvApplicationsView.modalTerms.actions.accept')}</span>
              </Button>

              <ButtonLink
                href={moreInfoLink}
                target="_blank"
                variant="tertiaryEmerald"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  {t('cvApplicationsView.modalTerms.actions.moreInfo')}
                </span>
              </ButtonLink>
            </StyledModalActions>
          )}
        </StyledWrapper>

        <GlobalStyles />
      </Modal>
    </ReactModal>
  );
};

export default CVApplicationsTermsModalComponent;
